// ── ExpiringLRDialog.jsx ───────────────────────────────────────────
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  LocalShipping as TruckIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Visibility,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { T } from "../../../theme";
import { pdfGenerator } from "./pdfGenerator";
import { expiringEwayBills } from "../../../Services/Services";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.white },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const getEwayExpiryStatus = (validityDate) => {
  if (!validityDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(validityDate);
  expiry.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0)
    return {
      status: "expired",
      label: "Expired",
      color: "#dc2626",
      bg: "#fee2e2",
      priority: 0,
      days: diffDays,
    };
  if (diffDays === 0)
    return {
      status: "today",
      label: "Expires Today",
      color: "#dc2626",
      bg: "#fee2e2",
      priority: 1,
      days: diffDays,
    };
  if (diffDays === 1)
    return {
      status: "tomorrow",
      label: "Expires Tomorrow",
      color: "#d97706",
      bg: "#fef3c7",
      priority: 2,
      days: diffDays,
    };
  if (diffDays === 2)
    return {
      status: "soon",
      label: "Expires in 2 days",
      color: "#d97706",
      bg: "#fef3c7",
      priority: 3,
      days: diffDays,
    };
  return null;
};

// ── Sub Components ─────────────────────────────────────────────────
const StatusPill = ({ color, bg, label, count, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.6,
      px: 1.25,
      py: 0.5,
      ...sq,
      border: `1.5px solid ${active ? color : T.border}`,
      bgcolor: active ? bg : T.white,
      cursor: "pointer",
      transition: "all 0.18s",
      "&:hover": { bgcolor: bg, borderColor: color },
    }}
  >
    <Box
      sx={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        bgcolor: color,
        flexShrink: 0,
      }}
    />
    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color }}>
      {label}
    </Typography>
    <Box
      sx={{
        px: 0.6,
        py: 0.1,
        ...sq,
        bgcolor: color,
        color: "#fff",
        fontSize: "0.6rem",
        fontWeight: 800,
        lineHeight: 1.4,
        minWidth: 16,
        textAlign: "center",
      }}
    >
      {count}
    </Box>
  </Box>
);

const StatBox = ({ label, value, color, bg, border }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 90,
      px: 1.25,
      py: 0.75,
      ...sq,
      bgcolor: bg || T.primaryLight,
      border: `1.5px solid ${border || T.border}`,
      textAlign: "center",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.58rem",
        fontWeight: 700,
        color: T.textMuted,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        mb: 0.15,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "0.92rem", fontWeight: 800, color: color || T.primary }}
    >
      {value}
    </Typography>
  </Box>
);

const SmallAction = ({ tip, icon, color, bg, bd, hBg, onClick }) => (
  <Tooltip title={tip} arrow placement="top">
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 22,
          height: 22,
          ...sq,
          color,
          bgcolor: bg,
          border: `1px solid ${bd}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hBg,
            borderColor: color,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </span>
  </Tooltip>
);

// ── Main Component ─────────────────────────────────────────────────
export const ExpiringLRDialog = ({ open, onClose, onEdit, onPreview }) => {
  const [expiringData, setExpiringData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("today");
  const [sortBy, setSortBy] = useState("priority");

  // ── Fetch from dedicated endpoint ──
  const fetchExpiring = async () => {
    setLoading(true);
    setError(null);
    try {
      // ← include_expired: true pass karo
      const res = await expiringEwayBills({ include_expired: true });

      const records = (Array.isArray(res) ? res : res?.results || [])
        .map((row) => ({
          ...row,
          _expiry: getEwayExpiryStatus(row.eway_validity_date),
        }))
        .filter((row) => row._expiry !== null);

      setExpiringData(records);
    } catch (err) {
      console.error("Failed to fetch expiring e-way bills:", err);
      setError("Failed to load data. Please try again.");
      setExpiringData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch when dialog opens
  useEffect(() => {
    if (open) {
      fetchExpiring();
      setSearch("");
      setStatusFilter("today");
      setSortBy("priority");
    }
  }, [open]);

  // ── Counts ──
  const counts = useMemo(
    () => ({
      all: expiringData.length,
      expired: expiringData.filter((r) => r._expiry.status === "expired")
        .length,
      today: expiringData.filter((r) => r._expiry.status === "today").length,
      tomorrow: expiringData.filter((r) => r._expiry.status === "tomorrow")
        .length,
      soon: expiringData.filter((r) => r._expiry.status === "soon").length,
    }),
    [expiringData],
  );

  // ── Filtered + Sorted ──
  const filtered = useMemo(() => {
    let list = expiringData;
    if (statusFilter !== "all") {
      list = list.filter((r) => r._expiry.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(
        (r) =>
          (r.truck_no || "").toLowerCase().includes(q) ||
          (r.party_name || "").toLowerCase().includes(q) ||
          (r.eway_bill_no || "").toLowerCase().includes(q) ||
          (r.lr_number || "").toLowerCase().includes(q),
      );
    }
    list = [...list].sort((a, b) => {
      if (sortBy === "priority") return a._expiry.priority - b._expiry.priority;
      if (sortBy === "date")
        return new Date(a.eway_validity_date) - new Date(b.eway_validity_date);
      if (sortBy === "truck")
        return (a.truck_no || "").localeCompare(b.truck_no || "");
      return 0;
    });
    return list;
  }, [expiringData, statusFilter, search, sortBy]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          ...sq,
          height: "88vh",
          maxWidth: "1100px", // ← reduced width
          mx: "auto", // ← centers it
          border: `1.5px solid ${T.border}`,
          boxShadow: "0 8px 40px rgba(220,38,38,0.12)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.18)",
            backdropFilter: "blur(3px)",
          },
        },
      }}
    >
           {/* ── Header ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
          px: 2.5,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              ...sq,
              bgcolor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningIcon sx={{ fontSize: 16, color: "#fff" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                color: "#fff",
                fontSize: "0.88rem",
                lineHeight: 1.2,
              }}
            >
              Expiring E-way Bills
            </Typography>
            <Typography
              sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.75)" }}
            >
              Showing{" "}
              <Box
                component="span"
                sx={{ color: "#fff", fontWeight: 800 }}
              >
                {filtered.length}
              </Box>{" "}
              of{" "}
              <Box
                component="span"
                sx={{ color: "#fca5a5", fontWeight: 800 }}
              >
                {expiringData.length}
              </Box>{" "}
              expiring records
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Tooltip title="Refresh" arrow>
            <IconButton
              size="small"
              onClick={fetchExpiring}
              disabled={loading}
              sx={{
                color: "rgba(255,255,255,0.8)",
                p: 0.5,
                ...sq,
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: "#fff" },
              }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.8)",
              p: 0.5,
              ...sq,
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: "#fff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Summary Stats ── */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "#fafafa",
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <StatBox
            label="Total"
            value={counts.all}
            color="#dc2626"
            bg="#fee2e2"
            border="#fca5a5"
          />
          <StatBox
            label="Expired"
            value={counts.expired}
            color="#dc2626"
            bg="#fee2e2"
            border="#fca5a5"
          />
          <StatBox
            label="Today"
            value={counts.today}
            color="#dc2626"
            bg="#fee2e2"
            border="#fca5a5"
          />
          <StatBox
            label="Tomorrow"
            value={counts.tomorrow}
            color="#d97706"
            bg="#fef3c7"
            border="#fcd34d"
          />
          <StatBox
            label="2 Days"
            value={counts.soon}
            color="#d97706"
            bg="#fef3c7"
            border="#fcd34d"
          />
        </Stack>
      </Box>

      {/* ── Toolbar ── */}
      <Box
        sx={{
          px: 2,
          py: 0.75,
          bgcolor: T.white,
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search truck, party, e-way bill, LR…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 14, color: T.primary }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearch("")}
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 13, color: T.textMuted }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ ...filterInputSx, minWidth: 220, maxWidth: 280 }}
        />

        {/* Filter Pills - Order changed: Today, Tomorrow, 2 Days, Expired, All */}
        <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
          {[
            { key: "today", label: "Today", color: "#dc2626", bg: "#fee2e2" },
            {
              key: "tomorrow",
              label: "Tomorrow",
              color: "#d97706",
              bg: "#fef3c7",
            },
            { key: "soon", label: "2 Days", color: "#d97706", bg: "#fef3c7" },
            {
              key: "expired",
              label: "Expired",
              color: "#dc2626",
              bg: "#fee2e2",
            },
            { key: "all", label: "All", color: T.primary, bg: T.primaryLight },
          ].map(({ key, label, color, bg }) => (
            <StatusPill
              key={key}
              label={label}
              color={color}
              bg={bg}
              count={counts[key]}
              active={statusFilter === key}
              onClick={() => setStatusFilter(key)}
            />
          ))}
        </Stack>

        {/* <Box
          sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <Typography
            sx={{ fontSize: "0.65rem", color: T.textMuted, fontWeight: 600 }}
          >
            Sort:
          </Typography>
          {[
            { key: "priority", label: "Priority" },
            { key: "date", label: "Date" },
            { key: "truck", label: "Truck" },
          ].map(({ key, label }) => (
            <Box
              key={key}
              onClick={() => setSortBy(key)}
              sx={{
                px: 0.75,
                py: 0.3,
                ...sq,
                border: `1.5px solid ${sortBy === key ? T.primary : T.border}`,
                bgcolor: sortBy === key ? T.primaryLight : T.white,
                color: sortBy === key ? T.primary : T.textSub,
                fontSize: "0.62rem",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: T.primary,
                  color: T.primary,
                  bgcolor: T.primaryLight,
                },
              }}
            >
              {label}
            </Box>
          ))}
        </Box> */}
      </Box>

      {/* ── Content ── */}
      <DialogContent
        sx={{ p: 0, flex: 1, overflow: "hidden", bgcolor: "#fafafa" }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1.5,
            }}
          >
            <CircularProgress sx={{ color: "#dc2626" }} size={32} />
            <Typography sx={{ fontSize: "0.75rem", color: T.textSub }}>
              Loading expiring records…
            </Typography>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                ...sq,
                bgcolor: "#fee2e2",
                border: `1.5px solid #fca5a5`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningIcon sx={{ fontSize: 24, color: "#dc2626" }} />
            </Box>
            <Typography
              sx={{ fontWeight: 700, color: T.text, fontSize: "0.85rem" }}
            >
              {error}
            </Typography>
            <Button
              size="small"
              onClick={fetchExpiring}
              startIcon={<RefreshIcon sx={{ fontSize: 13 }} />}
              sx={{ ...ghostBtn, height: 30 }}
            >
              Retry
            </Button>
          </Box>
        ) : filtered.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                ...sq,
                bgcolor: "#f0fdf4",
                border: `1.5px solid #86efac`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TruckIcon sx={{ fontSize: 26, color: "#16a34a" }} />
            </Box>
            <Typography
              sx={{ fontWeight: 700, color: T.text, fontSize: "0.88rem" }}
            >
              {expiringData.length === 0
                ? "All E-way Bills are valid! 🎉"
                : "No records match your search"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: T.textSub }}>
              {expiringData.length === 0
                ? "No e-way bills are expiring within 2 days."
                : "Try adjusting the search or filter."}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TableContainer
              sx={{
                ...sq,
                border: `1px solid ${T.border}`,
                bgcolor: T.white,
                flex: 1,
                minHeight: 0,
                overflow: "auto",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  "& td, & th": {
                    py: 0,
                    px: 0.75,
                    fontSize: "0.7rem",
                    borderBottom: `1px solid #e5e7eb`,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    fontSize: "0.68rem",
                    letterSpacing: "0.01em",
                    borderBottom: `2px solid #e5e7eb`,
                    height: 28,
                  },
                  "& td": { height: 28 },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      { l: "#", w: 32 },
                      { l: "Status", w: 110 },
                      { l: "Validity", w: 90 },
                      { l: "Days", w: 65 },
                      { l: "Truck No", w: 85 },
                      { l: "LR No", w: 70 },
                      { l: "Party Name", w: 130 },
                      { l: "E-way Bill No", w: 110 },
                      { l: "From", w: 90 },
                      { l: "To", w: 90 },
                      { l: "Loading Date", w: 90 },
                      { l: "Net Payable", w: 90 },
                      { l: "Phone", w: 90 },
                      { l: "Actions", w: 80, sticky: true },
                    ].map(({ l, w, sticky }) => (
                      <TableCell
                        key={l}
                        sx={{
                          minWidth: w,
                          bgcolor: `${T.headerBg} !important`,
                          color: `${T.headerText} !important`,
                          ...(sticky && {
                            position: "sticky",
                            right: 0,
                            zIndex: 20,
                          }),
                        }}
                      >
                        {l}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered.map((row, idx) => {
                    const { _expiry } = row;
                    const isEven = idx % 2 === 0;
                    const rowBg = isEven
                      ? `${_expiry.bg}88`
                      : `${_expiry.bg}55`;
                    const daysText =
                      _expiry.days < 0
                        ? `${Math.abs(_expiry.days)}d ago`
                        : _expiry.days === 0
                          ? "Today"
                          : `${_expiry.days}d left`;

                    return (
                      <TableRow
                        key={row.freight_id}
                        sx={{
                          "& > td": {
                            bgcolor: rowBg,
                            transition: "background-color 0.12s",
                          },
                          "&:hover > td": {
                            bgcolor: `${_expiry.bg} !important`,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              color: T.textMuted,
                            }}
                          >
                            {idx + 1}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={_expiry.label}
                            size="small"
                            sx={{
                              fontSize: "0.58rem",
                              height: 15,
                              ...sq,
                              bgcolor: _expiry.bg,
                              color: _expiry.color,
                              border: `1.5px solid ${_expiry.color}`,
                              fontWeight: 800,
                              "& .MuiChip-label": { px: 0.6 },
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              color: _expiry.color,
                            }}
                          >
                            {formatDate(row.eway_validity_date)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-flex",
                              px: 0.6,
                              py: 0.15,
                              ...sq,
                              bgcolor: _expiry.color,
                              color: "#fff",
                              fontSize: "0.58rem",
                              fontWeight: 800,
                            }}
                          >
                            {daysText}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.7rem",
                              color: T.primary,
                            }}
                          >
                            {row.truck_no || "—"}
                          </Typography>
                        </TableCell>

                        <TableCell>{row.lr_number || "—"}</TableCell>

                        <TableCell>
                          <Tooltip title={row.party_name || "—"} arrow>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: T.text,
                                fontWeight: 500,
                                maxWidth: 130,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.party_name || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontFamily: "monospace",
                              fontWeight: 600,
                              color: T.text,
                            }}
                          >
                            {row.eway_bill_no || "—"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Tooltip title={row.from_location || "—"} arrow>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: T.text,
                                maxWidth: 90,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.from_location || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Tooltip title={row.to_location || "—"} arrow>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: T.text,
                                maxWidth: 90,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.to_location || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell>{formatDate(row.loading_date)}</TableCell>

                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.7rem",
                              color: T.primary,
                            }}
                          >
                            ₹{row.net_payable || 0}
                          </Typography>
                        </TableCell>

                        <TableCell>{row.phone_no || "—"}</TableCell>

                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            bgcolor: `${rowBg} !important`,
                            zIndex: 5,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.4}
                            justifyContent="center"
                          >
                            <SmallAction
                              tip="Preview"
                              icon={<Visibility />}
                              color="#1976d2"
                              bg="#e3f2fd"
                              bd="#bbdefb"
                              hBg="#bbdefb"
                              onClick={() => onPreview && onPreview(row)}
                            />
                            <SmallAction
                              tip="Edit"
                              icon={<EditIcon />}
                              color="#757575"
                              bg="#f5f5f5"
                              bd="#e0e0e0"
                              hBg="#eeeeee"
                              onClick={() => onEdit && onEdit(row)}
                            />
                            <SmallAction
                              tip="Print"
                              icon={<PrintIcon />}
                              color={T.success}
                              bg={T.successBg}
                              bd={T.successBorder}
                              hBg="#d1fae5"
                              onClick={() => pdfGenerator(row)}
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
